import { useState } from "react";
import { Image as ImageIcon, FileText, X, Lightbulb } from "lucide-react";

import { useResource } from "../lib/useApi";
import { Sheet, Glass, Skeleton, Err, Empty } from "../ui";

/**
 * DesignsSheet — the client's own renders and drawings.
 *
 * The "Ideas" tab that used to live here is gone. It read `j.items || j.ideas`
 * from /api/design-ideas.php, which returns neither — the payload is
 * `{ projects: [ { shots: [ {url, room} ] } ], filters: { rooms } }` — so it
 * rendered nothing against the real endpoint, and would have thrown away 96
 * room-tagged pictures across 14 rooms if it had worked.
 *
 * Ideas is now its own screen (screens/Inspirations.jsx), which is what it was
 * before this app replaced the Shadow-DOM widget. Conflating the two was wrong
 * anyway: "yours" is renders of this client's home, "ideas" is the studio's
 * public portfolio, and a client asking why "their" design is of somebody
 * else's kitchen is the predictable result of putting them behind one tab.
 */

export function DesignsSheet({ ctx, onClose }) {
  return (
    <Sheet title="Your designs" onClose={onClose}>
      <button className="btn block" style={{ marginBottom: 16 }} onClick={() => ctx.open("ideas")}>
        <Lightbulb size={15} /> Browse the idea gallery
      </button>
      <Mine pid={ctx.pid} />
    </Sheet>
  );
}

function Mine({ pid }) {
  const { data, loading, error, reload } = useResource(`/portal/renders?project_id=${pid}`);
  const [lightbox, setLightbox] = useState(null);

  if (loading) return <Grid><Skeleton h={150} /><Skeleton h={150} /></Grid>;
  if (error) return <Err onDismiss={reload}>{error}</Err>;
  if (!data?.length) {
    return <Empty icon={ImageIcon} title="No designs shared yet" note="Renders and drawings appear here as your designer releases them." />;
  }

  return (
    <>
      <Grid>
        {data.map((r) => {
          const isPdf = String(r.file_type || "").toLowerCase().includes("pdf");
          return isPdf ? (
            <Glass as="a" key={r.id} href={r.image_url} target="_blank" rel="noopener noreferrer" className="like" style={{ width: "auto" }}>
              <span className="like-art" style={{ display: "grid", placeItems: "center", background: "var(--cyan-tint)" }}>
                <FileText size={30} strokeWidth={1.3} color="var(--mauve-deep)" />
              </span>
              <span className="like-body">
                <b>{r.title}</b>
                <small>{[r.room, r.version && `v${r.version}`].filter(Boolean).join(" · ")} · PDF</small>
              </span>
            </Glass>
          ) : (
            <Glass as="button" key={r.id} className="like" style={{ width: "auto" }} onClick={() => setLightbox(r)}>
              <span className="like-art" style={{ backgroundImage: `url(${r.image_url})` }} />
              <span className="like-body">
                <b>{r.title}</b>
                <small>{[r.room, r.version && `v${r.version}`].filter(Boolean).join(" · ") || "Render"}</small>
              </span>
            </Glass>
          );
        })}
      </Grid>

      {lightbox && (
        <div
          className="scrim"
          style={{ zIndex: 80, display: "grid", placeItems: "center", padding: 16 }}
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-label={lightbox.title}
        >
          <img
            src={lightbox.image_url}
            alt={lightbox.title}
            style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 18, boxShadow: "0 30px 60px -20px rgba(0,0,0,.6)" }}
          />
          <button className="bar-btn" style={{ position: "fixed", top: 20, right: 20 }} aria-label="Close">
            <X size={18} />
          </button>
          {lightbox.description && (
            <p style={{ color: "#fff", maxWidth: 420, textAlign: "center", fontSize: ".86rem" }}>{lightbox.description}</p>
          )}
        </div>
      )}
    </>
  );
}

function Grid({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}
